/**
 * The site stores formatted text as plain text: paragraphs split by a blank line, "- " lines as bullet points, **bold**,
 * _italic_ and [label](href) (see Emphasis and textBlocks on the site). The rich text field edits it as HTML and converts
 * both ways, so the stored text and the site stay as they are.
 */

const LIST_ITEM = /^\s*[-•]\s+/;
const TOKEN = /(\*\*[^*]+\*\*|(?<![\w])_[^_\n]+_(?![\w])|\[[^\]]+\]\([^)\s]+\))/g;
const LINK = /^\[([^\]]+)\]\(([^)\s]+)\)$/;

function escapeHtml(text: string): string {
  return text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}

/** **bold**, _italic_ and links of one line or item as HTML; a marker inside another (bold in a link) is converted too. */
function inlineHtml(text: string): string {
  return text
    .split(TOKEN)
    .map((part) => {
      if (part.length > 4 && part.startsWith('**') && part.endsWith('**')) return `<strong>${inlineHtml(part.slice(2, -2))}</strong>`;
      if (part.length > 2 && part.startsWith('_') && part.endsWith('_')) return `<em>${inlineHtml(part.slice(1, -1))}</em>`;
      const link = LINK.exec(part);
      if (link) return `<a href="${escapeHtml(link[2])}">${inlineHtml(link[1])}</a>`;
      return escapeHtml(part).replace(/\n/g, '<br>');
    })
    .join('');
}

/** The stored text as HTML for the editor: <p> per paragraph, <ul> per run of "- " lines. */
export function textToHtml(text: string): string {
  const html: string[] = [];
  let items: string[] = [];
  const flushList = () => {
    if (items.length > 0) html.push(`<ul>${items.map((item) => `<li><p>${inlineHtml(item)}</p></li>`).join('')}</ul>`);
    items = [];
  };
  for (const paragraph of text.split(/\n\s*\n/).map((part) => part.trim()).filter(Boolean)) {
    let lines: string[] = [];
    const flushLines = () => {
      if (lines.length > 0) html.push(`<p>${inlineHtml(lines.join('\n'))}</p>`);
      lines = [];
    };
    for (const line of paragraph.split('\n')) {
      if (LIST_ITEM.test(line)) {
        flushLines();
        const item = line.replace(LIST_ITEM, '').trim();
        if (item) items.push(item);
      } else {
        flushList();
        lines.push(line);
      }
    }
    flushLines();
  }
  flushList();
  return html.join('');
}

/** Spaces stay outside the markers ("**vet** tekst", not "**vet **tekst"), or the site would not see them as bold. */
function wrap(marker: string, inner: string): string {
  const match = /^(\s*)([\s\S]*?)(\s*)$/.exec(inner);
  const [, before, core, after] = match || ['', '', inner, ''];
  return core ? `${before}${marker}${core}${marker}${after}` : inner;
}

function inlineText(node: Node): string {
  if (node.nodeType === Node.TEXT_NODE) return (node.textContent || '').replace(/\u00a0/g, ' ');
  if (!(node instanceof HTMLElement)) return '';
  const inner = Array.from(node.childNodes).map(inlineText).join('');
  switch (node.tagName) {
    case 'BR':
      return '\n';
    case 'STRONG':
    case 'B':
      return wrap('**', inner);
    case 'EM':
    case 'I':
      return wrap('_', inner);
    case 'A': {
      const href = node.getAttribute('href') || '';
      return href && inner.trim() ? `[${inner.trim()}](${href.trim()})` : inner;
    }
    default:
      return inner;
  }
}

/** The editor's HTML back to the stored text. */
export function htmlToText(html: string): string {
  const body = new DOMParser().parseFromString(html, 'text/html').body;
  const blocks: string[] = [];
  for (const node of Array.from(body.childNodes)) {
    if (node instanceof HTMLElement && (node.tagName === 'UL' || node.tagName === 'OL')) {
      const items = Array.from(node.children)
        .map((item) => inlineText(item).replace(/\s*\n\s*/g, ' ').trim())
        .filter(Boolean);
      if (items.length > 0) blocks.push(items.map((item) => `- ${item}`).join('\n'));
    } else {
      const text = inlineText(node)
        .split('\n')
        .map((line) => line.trim())
        .join('\n')
        .trim();
      if (text) blocks.push(text);
    }
  }
  return blocks.join('\n\n');
}
